import * as PIXI from 'pixi.js';
import { LayoutContainer, LayoutText } from '@pixi/layout/components'; // eslint-disable-line
import { APP_WIDTH, APP_HEIGHT } from '../../utils';

const MODAL_BOX_WIDTH = 850;
const MODAL_BOX_HEIGHT = 550;
const MODAL_BOX_HEADER_HEIGHT = 130;
const MODAL_BOX_RADIUS = 40;
const MODAL_BOX_BODY_BORDER_WIDTH = 5;

export default class AttentionPopup extends PIXI.Container {
    constructor(message, width = APP_WIDTH, height = APP_HEIGHT) {
        super();

        const container = new LayoutContainer();
        container.label = 'confirmSwapModalContainer';
        this.overlay = container;
        container.layout = {
            width: width,
            height: height,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 50,
        };
        container.eventMode = 'static';
        container.on('pointerdown', (event) => {
            event.stopPropagation();
            this.onBackgroundClicked?.();
        });

        const modalBoxContainer = new PIXI.Container();
        modalBoxContainer.layout = {
            width: MODAL_BOX_WIDTH,
            height: MODAL_BOX_HEIGHT,
            flexDirection: 'column',
        };
        container.addChild(modalBoxContainer);

        const modalBoxMask = new PIXI.Graphics();
        modalBoxMask.roundRect(0, 0, MODAL_BOX_WIDTH, MODAL_BOX_HEIGHT, 40);
        modalBoxMask.fill({ color: 'white' });
        modalBoxContainer.addChild(modalBoxMask);
        modalBoxContainer.mask = modalBoxMask;

        const modalBoxHeader = new PIXI.Container();
        modalBoxHeader.layout = {
            width: MODAL_BOX_WIDTH,
            height: MODAL_BOX_HEADER_HEIGHT,
            flexDirection: 'row',
            alignItems: 'center',
            paddingLeft: 40,
            gap: 20,
        };
        modalBoxHeader.zIndex = 1;
        modalBoxContainer.addChild(modalBoxHeader);

        const modalBoxHeaderGradient = new PIXI.FillGradient({
            type: 'linear',
            start: { x: 0, y: 0.5 },
            end: { x: 1, y: 0.5 },
            colorStops: [
                { offset: 0, color: '#2a74a9' },
                { offset: 1, color: '#e9e9e9' },
            ],
        });

        const modalBoxHeaderBackground = new PIXI.Graphics();
        modalBoxHeaderBackground.rect(0, 0, MODAL_BOX_WIDTH, MODAL_BOX_HEADER_HEIGHT);
        modalBoxHeaderBackground.fill(modalBoxHeaderGradient);
        modalBoxHeader.addChild(modalBoxHeaderBackground);

        const attentionIcon = new PIXI.Sprite(PIXI.Assets.get('attentionIcon'));
        attentionIcon.layout = {
            width: 100,
            height: 100,
        };
        modalBoxHeader.addChild(attentionIcon);

        const modalBoxHeaderText = new PIXI.Text({
            text: 'Attention',
            style: {
                fontFamily: 'Open Sans',
                fontSize: 60,
                fontWeight: '700',
                fill: '#ffffff',
            },
        });
        modalBoxHeaderText.layout = {
            width: 400,
            height: 60,
            objectPosition: 'left',
        };
        modalBoxHeader.addChild(modalBoxHeaderText);

        const modalBoxBody = new PIXI.Container();
        modalBoxBody.layout = {
            width: MODAL_BOX_WIDTH,
            height: MODAL_BOX_HEIGHT - MODAL_BOX_HEADER_HEIGHT,
        };
        modalBoxContainer.addChild(modalBoxBody);

        const modalBoxBodyBackground = new PIXI.Graphics();
        modalBoxBodyBackground.roundRect(MODAL_BOX_BODY_BORDER_WIDTH / 2, -MODAL_BOX_RADIUS, MODAL_BOX_WIDTH - MODAL_BOX_BODY_BORDER_WIDTH, MODAL_BOX_HEIGHT - MODAL_BOX_HEADER_HEIGHT + (MODAL_BOX_RADIUS - MODAL_BOX_BODY_BORDER_WIDTH / 2), MODAL_BOX_RADIUS);
        modalBoxBodyBackground.fill({ color: '#e9e9e9' });
        modalBoxBodyBackground.stroke({ color: '#c3c3c5', width: MODAL_BOX_BODY_BORDER_WIDTH });
        modalBoxBody.addChild(modalBoxBodyBackground);

        const chopperMask = new PIXI.Graphics();
        chopperMask.roundRect(MODAL_BOX_BODY_BORDER_WIDTH, -MODAL_BOX_RADIUS, MODAL_BOX_WIDTH - MODAL_BOX_BODY_BORDER_WIDTH*2, MODAL_BOX_HEIGHT - MODAL_BOX_HEADER_HEIGHT + (MODAL_BOX_RADIUS - MODAL_BOX_BODY_BORDER_WIDTH), MODAL_BOX_RADIUS);
        chopperMask.fill({ color: 'white' });
        modalBoxBody.addChild(chopperMask);

        const attentionChopperIcon = new PIXI.Sprite(PIXI.Assets.get('attentionChopperIcon'));
        attentionChopperIcon.x = -20;
        attentionChopperIcon.mask = chopperMask;
        modalBoxBody.addChild(attentionChopperIcon);

        const modalBoxBodyText = new LayoutText({
            text: message,
            style: {
                fontFamily: 'Open Sans',
                fontSize: 40,
                fontWeight: '600',
                fill: '#000000',
                wordWrap: true,
            },
        });
        modalBoxBodyText.layout = {
            width: MODAL_BOX_WIDTH - 300,
            height: 100,
            position: 'absolute',
            top: 90,
            right: 20,
        };
        modalBoxBody.addChild(modalBoxBodyText);

        const buttonsContainer = new LayoutContainer();
        buttonsContainer.layout = {
            width: MODAL_BOX_WIDTH,
            height: 107,
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 20,
            position: 'absolute',
            bottom: 40,
        };
        modalBoxBody.addChild(buttonsContainer);

        const cancelButton = new PIXI.Sprite(PIXI.Assets.get('attentionPopup_cancelButton'));
        cancelButton.layout = {
            width: 363,
            height: 107,
        };
        buttonsContainer.addChild(cancelButton);
        cancelButton.eventMode = 'static';
        cancelButton.on('pointerdown', () => {
            this.onCancelButtonClicked?.();
        });

        const proceedButton = new PIXI.Sprite(PIXI.Assets.get('attentionPopup_proceedButton'));
        proceedButton.layout = {
            width: 363,
            height: 107,
        };
        buttonsContainer.addChild(proceedButton);
        proceedButton.eventMode = 'static';
        proceedButton.on('pointerdown', () => {
            this.onProceedButtonClicked?.();
        });

        this.addChild(container);
    }

    resize(width, height) {
        this.overlay?.layout?.setStyle({ width, height });
    }

    static async loadAssets() {
        await PIXI.Assets.load([
            { src: '/assets/decks/attention.png', alias: 'attentionIcon' },
            { src: '/assets/decks/attention_chopper.png', alias: 'attentionChopperIcon' },
            { src: '/assets/decks/cancel_swap.png', alias: 'attentionPopup_cancelButton' },
            { src: '/assets/decks/confirm_swap.png', alias: 'attentionPopup_proceedButton' },
        ]);
    }
}